import { useTranslations } from "next-intl";
// Locale-aware Link: plain next/link emitted unprefixed hrefs, so every footer
// link cost a 307 to the /{locale} version on the way in.
import { Link } from "@/navigation";
import Image from "next/image";
// Same wordmark asset the navbar uses, so the footer brand matches the header
// instead of being a separate gradient-text rendering of the name.
import navbarIcon from "@/assets/images/navbar_icon.svg";
import React from "react";

const Footer: React.FC = () => {
  const t = useTranslations("Landing");

  return (
    <footer className="bg-gray-900/80 border-t border-gray-700/50 shadow-inner relative z-10">
      {/* Full-width gradient separator */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-blue-500/30"></div>

      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center py-4">
          <div className="mb-3 md:mb-0">
            <Link href="/" className="inline-block mb-1">
              <Image
                src={navbarIcon}
                alt="Atlivo"
                width={32}
                height={32}
                className="h-7 w-auto max-w-none"
              />
            </Link>
            <p className="text-sm text-gray-400">{t("Footer.Copyright", { year: new Date().getFullYear() })}</p>
          </div>

          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 md:gap-8">
            <Link
              href="/faq"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t("Footer.NavLinks.Faq")}
            </Link>

            <Link
              href="/docs/terms"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t("Footer.NavLinks.Terms")}
            </Link>

            <Link
              href="/docs/privacy"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t("Footer.NavLinks.Privacy")}
            </Link>

            <Link
              href="/contact"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {t("Footer.NavLinks.Contact")}
            </Link>
          </div>
        </div>

        {/* ANPC, SAL and SOL links */}
        <div className="border-t border-gray-700/50 py-3">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-4">
            <a
              href="https://anpc.ro"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
            >
              ANPC
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>

            <div className="flex items-center gap-3">
              <a
                href="https://anpc.ro/ce-este-sal/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/15 transition-colors rounded px-2 py-1 flex items-center gap-1 text-sm"
              >
                <span className="text-gray-300">SAL</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>

              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/15 transition-colors rounded px-2 py-1 flex items-center gap-1 text-sm"
              >
                <span className="text-gray-300">SOL</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
